/**
 * Card grouping primitives shared by deck, list and set views.
 *
 * The grouping rules (which buckets exist, their labels and their order) are identical
 * everywhere; only the card shape differs, so callers supply a `toGroupable` mapper.
 */
import { TYPE_CATEGORY_LABELS } from '@/lib/cardUtils';
import type { TypeCategory } from '@/types/card';

export type CardGroupByMode = 'none' | 'type' | 'cmc' | 'color_identity';

export interface CardGroup<T> {
  key: string;
  label: string;
  cards: T[];
  totalQuantity: number;
  sortOrder: number;
}

/** The facets grouping needs, projected out of whatever enriched card type the caller holds. */
export interface GroupableCard {
  typeCategory: TypeCategory;
  cmc: number;
  colorIdentity: string[];
  quantity: number;
}

/** CMC bucket label for grouping (7+ collapses into one bucket). */
export function getCmcLabel(cmc: number): string {
  if (cmc === 0) return '0 Mana';
  if (cmc >= 7) return '7+ Mana';
  return `${cmc} Mana`;
}

/** Color identity bucket: a single colour name, 'Multicolor', or 'Colorless'. */
export function getColorIdentityGroup(colorIdentity: string[]): string {
  if (!colorIdentity || colorIdentity.length === 0) return 'Colorless';
  if (colorIdentity.length > 1) return 'Multicolor';

  const colorMap: Record<string, string> = {
    W: 'White',
    U: 'Blue',
    B: 'Black',
    R: 'Red',
    G: 'Green',
  };

  return colorMap[colorIdentity[0]] ?? 'Colorless';
}

export function getColorIdentitySortOrder(group: string): number {
  const order = ['White', 'Blue', 'Black', 'Red', 'Green', 'Multicolor', 'Colorless'];
  const idx = order.indexOf(group);
  return idx === -1 ? 999 : idx;
}

export function getTypeSortOrder(type: TypeCategory): number {
  const order: TypeCategory[] = ['creature', 'planeswalker', 'instant', 'sorcery', 'artifact', 'enchantment', 'battle', 'land', 'other'];
  const idx = order.indexOf(type);
  return idx === -1 ? 999 : idx;
}

export interface GroupBucket {
  key: string;
  label: string;
  sortOrder: number;
}

export interface GroupAssignment extends GroupBucket {
  /** Counted into the group's `totalQuantity`. */
  quantity: number;
}

function bucketFor(groupBy: CardGroupByMode, facets: GroupableCard): GroupBucket {
  switch (groupBy) {
    case 'cmc': {
      const cmc = facets.cmc;
      return { key: String(Math.min(cmc, 7)), label: getCmcLabel(cmc), sortOrder: cmc >= 7 ? 7 : cmc };
    }
    case 'color_identity': {
      const label = getColorIdentityGroup(facets.colorIdentity);
      return { key: label, label, sortOrder: getColorIdentitySortOrder(label) };
    }
    case 'none':
      return { key: 'all', label: 'All Cards', sortOrder: 0 };
    case 'type':
    default: {
      const category = facets.typeCategory;
      return { key: category, label: TYPE_CATEGORY_LABELS[category], sortOrder: getTypeSortOrder(category) };
    }
  }
}

/**
 * Buckets cards with a caller-supplied assignment, for groupings outside `CardGroupByMode`
 * (e.g. list priority). `compareWithinGroup` sorts each group; omit it to keep insertion order.
 */
export function groupCardsByBucket<T>(
  cards: T[],
  assign: (card: T) => GroupAssignment,
  compareWithinGroup?: (a: T, b: T) => number,
): CardGroup<T>[] {
  const groups = new Map<string, CardGroup<T>>();

  for (const card of cards) {
    const { key, label, sortOrder, quantity } = assign(card);

    const existing = groups.get(key);
    if (existing) {
      existing.cards.push(card);
      existing.totalQuantity += quantity;
    } else {
      groups.set(key, { key, label, cards: [card], totalQuantity: quantity, sortOrder });
    }
  }

  if (compareWithinGroup) {
    for (const group of groups.values()) {
      group.cards.sort(compareWithinGroup);
    }
  }

  return Array.from(groups.values()).sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * Buckets cards by one of the standard modes and returns the groups in display order.
 * `compareWithinGroup` sorts each group's cards; omit it to keep insertion order.
 */
export function groupCards<T>(
  cards: T[],
  groupBy: CardGroupByMode,
  toGroupable: (card: T) => GroupableCard,
  compareWithinGroup?: (a: T, b: T) => number,
): CardGroup<T>[] {
  return groupCardsByBucket(
    cards,
    (card) => {
      const facets = toGroupable(card);
      return { ...bucketFor(groupBy, facets), quantity: facets.quantity };
    },
    compareWithinGroup,
  );
}
